import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { User, Crown, Wifi, WifiOff, Home, Star, Heart, FileText, MessageCircle } from 'lucide-react'
import { useUserStore } from '../stores/userStore'
import { useAuth } from '../contexts/AuthContext'
import { useEffect, useState } from 'react'
import UserStatusModal from './UserStatusModal'

const MobileHeader = () => {
  const location = useLocation()
  const { user } = useUserStore()
  const { userStatus, checkUserStatus } = useAuth()
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [isImporting, setIsImporting] = useState(false)

  // Check user status when component mounts or user changes
  useEffect(() => {
    if (user?.uid) {
      // Only check status if we don't have it yet or if it's been a while
      if (!userStatus || Date.now() - (userStatus as any).lastCheck > 30000) {
        checkUserStatus(user.uid)
      }
    }
  }, [user?.uid, checkUserStatus, userStatus])

  // Handle auto-import of missing charts
  const handleAutoImport = async () => {
    if (!user?.uid) return
    
    setIsImporting(true)
    try {
      await checkUserStatus(user.uid, true) // Enable auto-import
    } catch (error) {
      console.error('Auto-import failed:', error)
    } finally {
      setIsImporting(false)
    }
  }

  const getPageTitle = () => {
    switch (location.pathname) {
      case '/astrology':
        return 'Astrology'
      case '/wellness':
        return 'Wellness'
      case '/matchmaking':
        return 'Matchmaking'
      case '/user-journey':
        return 'Analytics'
      case '/subscription':
        return 'Plans'
      case '/profile':
        return 'Profile'
      default:
        return 'RRAASI'
    }
  }

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/profile', label: 'Profile', icon: User },
    { path: '/astrology', label: 'Astrology', icon: Star },
    { path: '/matchmaking', label: 'Matchmaking', icon: Heart },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/chat', label: 'Chat', icon: MessageCircle }
  ]

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-slate-900/90 backdrop-blur-lg border-b border-white/10"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex items-center justify-between px-4 py-2">
        {/* Title */}
        <Link to="/" className="flex items-center">
          {location.pathname === '/' ? (
            <span className="text-xl font-black bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent tracking-tight">
              RRAASI
            </span>
          ) : (
            <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {getPageTitle()}
            </span>
          )}
        </Link>

        {/* Right side actions */}
        <div className="flex items-center space-x-1">
          {user && (
            <>
              {/* User Status Indicator */}
              <button
                onClick={() => setIsStatusModalOpen(true)}
                className="flex items-center space-x-1 p-1 rounded-lg transition-all duration-200 hover:scale-105"
                title={userStatus?.astroAgentsLive ? `${userStatus.astroAgentsLive} Astro Agents live` : 'No Astro Agents available'}
              >
                {userStatus?.isOnline ? (
                  <div className="flex items-center space-x-1 p-1 rounded-lg bg-green-500/20 border border-green-500/30">
                    <Wifi className="w-3 h-3 text-green-400" />
                    <span className="text-xs text-green-400 font-medium">
                      {userStatus.astroAgentsLive > 0 ? `${userStatus.astroAgentsLive} Astro Agents` : 'Online'}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 p-1 rounded-lg bg-red-500/20 border border-red-500/30">
                    <WifiOff className="w-3 h-3 text-red-400" />
                    <span className="text-xs text-red-400 font-medium">
                      {userStatus?.astroAgentsLive > 0 ? `${userStatus.astroAgentsLive} Astro Agents` : 'Offline'}
                    </span>
                  </div>
                )}
              </button>
              
              <Link
                to="/subscription"
                className="p-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                <Crown className="w-3 h-3 text-yellow-400" />
              </Link>
              <Link
                to="/profile"
                className="p-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                <User className="w-3 h-3 text-white" />
              </Link>
            </>
          )}
        </div>
      </div>
      
      {/* User Status Modal */}
      <UserStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        userStatus={userStatus}
        onAutoImport={handleAutoImport}
        isImporting={isImporting}
      />
    </motion.header>
  )
}

export default MobileHeader 